import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../Utils/api";
import Spinner from "../Components/Spinner";
import { useSelector } from "react-redux";
import ArchiveCompanyModal from "../Components/Company/ArchiveCompanyModal";

export default function Companies() {
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    address: "",
    city: "",
    country: "",
    postCode: "",
    phone: "",
    email: "",
  });

  const user = useSelector((state) => state.user.user);

  useEffect(() => {
    if (user && user.deputyAccessLevel === "system_administrator") {
      getCompanies();
    }
  }, [user]);

  const getCompanies = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/company");
      setCompanies(response.data.companies || []);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch companies");
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setFormData({
      name: "",
      description: "",
      address: "",
      city: "",
      country: "",
      postCode: "",
      phone: "",
      email: "",
    });
    setSelectedCompany(null);
    setShowAddModal(true);
  };

  const handleEdit = (company) => {
    setFormData({
      name: company.name || "",
      description: company.description || "",
      address: company.address || "",
      city: company.city || "",
      country: company.country || "",
      postCode: company.postCode || "",
      phone: company.phone || "",
      email: company.email || "",
    });
    setSelectedCompany(company);
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Company name is required");
      return;
    }

    try {
      setIsLoading(true);
      if (selectedCompany) {
        // Update
        const response = await api.post("/company/update", {
          id: selectedCompany._id,
          updatedData: formData,
        });
        if (response.data.success) {
          toast.success("Company updated successfully");
          getCompanies();
          setShowEditModal(false);
        } else {
          toast.error(response.data.message || "Failed to update company");
        }
      } else {
        // Create
        const response = await api.post("/company", formData);
        if (response.data.success) {
          toast.success("Company created successfully");
          getCompanies();
          setShowAddModal(false);
        } else {
          toast.error(response.data.message || "Failed to create company");
        }
      }
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save company");
      setIsLoading(false);
    }
  };

  const handleArchive = async () => {
    try {
      if (!selectedCompany) return;

      setIsLoading(true);
      const response = await api.post(`/company/archive/${selectedCompany._id}`);
      setIsLoading(false);

      if (response.data.success) {
        toast.success("Company archived successfully");
        getCompanies();
        setShowArchiveModal(false);
        setSelectedCompany(null);
      } else {
        toast.error(response.data.message || "Failed to archive company");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to archive company");
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    try {
      if (!selectedCompany) return;

      setIsLoading(true);
      const response = await api.post(`/company/restore/${selectedCompany._id}`);
      setIsLoading(false);

      if (response.data.success) {
        toast.success("Company restored successfully");
        getCompanies();
        setShowArchiveModal(false);
        setSelectedCompany(null);
      } else {
        toast.error(response.data.message || "Failed to restore company");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to restore company");
      setIsLoading(false);
    }
  };

  // Only show for system administrators
  if (!user || user.deputyAccessLevel !== "system_administrator") {
    return (
      <div className="px-6 mt-20 mx-10 pb-20">
        <div className="text-center text-gray-500">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 mt-20 mx-10 pb-20">
      {isLoading && <Spinner />}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-600">Companies</h1>
        <button
          onClick={handleAdd}
          className="bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-600 transition"
        >
          <i className="fa fa-plus mr-2"></i>
          Add Company
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-md">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-4 text-left font-bold text-gray-700">Name</th>
              <th className="p-4 text-left font-bold text-gray-700">Description</th>
              <th className="p-4 text-left font-bold text-gray-700">Email</th>
              <th className="p-4 text-left font-bold text-gray-700">City</th>
              <th className="p-4 text-left font-bold text-gray-700">Country</th>
              <th className="p-4 text-center font-bold text-gray-700">Status</th>
              <th className="p-4 text-center font-bold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-4 text-center text-gray-500">
                  No companies found
                </td>
              </tr>
            ) : (
              companies.map((company) => (
                <tr key={company._id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-4 text-gray-700">{company.name}</td>
                  <td className="p-4 text-gray-700">{company.description || "-"}</td>
                  <td className="p-4 text-gray-700">{company.email || "-"}</td>
                  <td className="p-4 text-gray-700">{company.city || "-"}</td>
                  <td className="p-4 text-gray-700">{company.country || "-"}</td>
                  <td className="p-4 text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        company.archive
                          ? "bg-gray-200 text-gray-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {company.archive ? "Inactive" : "Active"}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => handleEdit(company)}
                        className="px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                      >
                        <i className="fa fa-edit mr-1"></i>
                        Edit
                      </button>
                      {company.archive ? (
                        <button
                          onClick={() => {
                            setSelectedCompany(company);
                            setShowArchiveModal(true);
                          }}
                          className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                          title="Restore"
                        >
                          <i className="fa fa-undo mr-1"></i>
                          Restore
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedCompany(company);
                            setShowArchiveModal(true);
                          }}
                          className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                          title="Archive"
                        >
                          <i className="fa fa-archive mr-1"></i>
                          Archive
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-700">
                {selectedCompany ? "Edit Company" : "Add Company"}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Post Code
                  </label>
                  <input
                    type="text"
                    name="postCode"
                    value={formData.postCode}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition"
                >
                  {selectedCompany ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Archive/Restore Confirmation Modal */}
      <ArchiveCompanyModal
        show={showArchiveModal}
        onClose={() => {
          setShowArchiveModal(false);
          setSelectedCompany(null);
        }}
        onConfirm={selectedCompany?.archive ? handleRestore : handleArchive}
        company={selectedCompany}
        isLoading={isLoading}
      />
    </div>
  );
}

